var _DotCmsClient_config, _DotCmsClient_requestOptions, _DotCmsClient_listeners;
import { __classPrivateFieldGet, __classPrivateFieldSet } from "tslib";
import { Content } from './content/content-api';
import { ErrorMessages } from './models';
import { isInsideEditor } from '../editor/sdk-editor';
function getHostURL(url) {
    try {
        return new URL(url);
    }
    catch (error) {
        return undefined;
    }
}
/**
 * `DotCmsClient` is a TypeScript class that provides methods to interact with the DotCMS REST API.
 * It requires a configuration object on instantiation, which includes the DotCMS URL, site ID, and authentication token.
 *
 * @class DotCmsClient
 *
 * @property {ClientConfig} config - The configuration object for the DotCMS client.
 *
 * @method constructor(config: ClientConfig) - Constructs a new instance of the DotCmsClient class.
 *
 * @method page.get(options: PageApiOptions): Promise<unknown> - Retrieves all the elements of any Page in your dotCMS system in JSON format.
 *
 * @method nav.get(options: NavApiOptions = { depth: 0, path: '/', languageId: 1 }): Promise<unknown> - Retrieves information about the dotCMS file and folder tree.
 *
 */
export class DotCmsClient {
    constructor(config = { dotcmsUrl: '', authToken: '', requestOptions: {}, siteId: '' }) {
        _DotCmsClient_config.set(this, void 0);
        _DotCmsClient_requestOptions.set(this, void 0);
        _DotCmsClient_listeners.set(this, []);
        this.page = {
            /**
             * `page.get` is an asynchronous method of the `DotCmsClient` class that retrieves all the elements of any Page in your dotCMS system in JSON format.
             * It takes a `PageApiOptions` object as a parameter and returns a Promise that resolves to the response from the DotCMS API.
             *
             * The Page API enables you to retrieve all the elements of any Page in your dotCMS system.
             * The elements may be retrieved in JSON format.
             *
             * @link https://www.dotcms.com/docs/latest/page-rest-api-layout-as-a-service-laas
             * @async
             * @param {PageApiOptions} options - The options for the Page API call.
             * @returns {Promise<unknown>} - A Promise that resolves to the response from the DotCMS API.
             * @throws {Error} - Throws an error if the options are not valid.
             */
            get: async (options) => {
                this.validatePageOptions(options);
                const queryParamsObj = {};
                for (const [key, value] of Object.entries(options)) {
                    if (value === undefined || key === 'path' || key === 'siteId')
                        continue;
                    if (key === 'personaId') {
                        queryParamsObj['com.dotmarketing.persona.id'] = String(value);
                    }
                    else if (key === 'mode' && value) {
                        queryParamsObj['mode'] = String(value);
                    }
                    else {
                        queryParamsObj[key] = String(value);
                    }
                }
                const queryHostId = options.siteId ?? __classPrivateFieldGet(this, _DotCmsClient_config, "f").siteId ?? '';
                if (queryHostId) {
                    queryParamsObj['host_id'] = queryHostId;
                }
                const queryParams = new URLSearchParams(queryParamsObj).toString();
                const formattedPath = options.path.startsWith('/') ? options.path : `/${options.path}`;
                const url = `${__classPrivateFieldGet(this, _DotCmsClient_config, "f").dotcmsUrl}/api/v1/page/json${formattedPath}${queryParams ? `?${queryParams}` : ''}`;
                const response = await fetch(url, __classPrivateFieldGet(this, _DotCmsClient_requestOptions, "f"));
                if (!response.ok) {
                    const error = {
                        status: response.status,
                        message: ErrorMessages[response.status] || response.statusText
                    };
                    console.error(error);
                    throw error;
                }
                return response.json().then((data) => data.entity);
            }
        };
        this.editor = {
            /**
             * `editor.on` is an asynchronous method of the `DotCmsClient` class that allows you to react to actions issued by the UVE.
             *
             *  NOTE: This is being used by the development team - This logic is probably varied or moved to another function/object.
             * @param action - The name of the name emitted by UVE
             * @param callbackFn - The function to execute when the UVE emits the action
             */
            on: (action, callbackFn) => {
                if (!isInsideEditor()) {
                    return;
                }
                if (action === 'changes') {
                    const messageCallback = (event) => {
                        if (event.data.name === 'SET_PAGE_DATA') {
                            callbackFn(event.data.payload);
                        }
                    };
                    window.addEventListener('message', messageCallback);
                    __classPrivateFieldGet(this, _DotCmsClient_listeners, "f").push({ event: 'message', callback: messageCallback, action });
                }
            },
            /**
             * `editor.off` is an synchronous method of the `DotCmsClient` class that allows you to stop listening and reacting to an action issued by UVE.
             *
             *  NOTE: This is being used by the development team - This logic is probably varied or moved to another function/object.
             * @param action
             */
            off: (action) => {
                const listenerIndex = __classPrivateFieldGet(this, _DotCmsClient_listeners, "f").findIndex((listener) => listener.action === action);
                if (listenerIndex !== -1) {
                    const listener = __classPrivateFieldGet(this, _DotCmsClient_listeners, "f")[listenerIndex];
                    window.removeEventListener(listener.event, listener.callback);
                    __classPrivateFieldGet(this, _DotCmsClient_listeners, "f").splice(listenerIndex, 1);
                }
            }
        };
        this.nav = {
            /**
             * `nav.get` is an asynchronous method of the `DotCmsClient` class that retrieves information about the dotCMS file and folder tree.
             * It takes a `NavApiOptions` object as a parameter (with default values) and returns a Promise that resolves to the response from the DotCMS API.
             *
             * The navigation REST API enables you to retrieve information about the dotCMS file and folder tree through REST API calls.
             * @link https://www.dotcms.com/docs/latest/navigation-rest-api
             * @async
             * @param {NavApiOptions} options - The options for the Nav API call. Defaults to `{ depth: 0, path: '/', languageId: 1 }`.
             * @returns {Promise<unknown>} - A Promise that resolves to the response from the DotCMS API.
             * @throws {Error} - Throws an error if the options are not valid.
             */
            get: async (options = { depth: 0, path: '/', languageId: 1 }) => {
                this.validateNavOptions(options);
                // Extract the 'path' from the options and prepare the rest as query parameters
                const { path, ...queryParamsOptions } = options;
                const queryParamsObj = {};
                Object.entries(queryParamsOptions).forEach(([key, value]) => {
                    if (value !== undefined) {
                        queryParamsObj[key] = String(value);
                    }
                });
                const queryParams = new URLSearchParams(queryParamsObj).toString();
                // Format the URL correctly depending on the 'path' value
                const formattedPath = path === '/' ? '/' : `/${path}`;
                const url = `${__classPrivateFieldGet(this, _DotCmsClient_config, "f").dotcmsUrl}/api/v1/nav${formattedPath}${queryParams ? `?${queryParams}` : ''}`;
                const response = await fetch(url, __classPrivateFieldGet(this, _DotCmsClient_requestOptions, "f"));
                return response.json();
            }
        };
        if (!config.dotcmsUrl) {
            throw new Error("Invalid configuration - 'dotcmsUrl' is required");
        }
        this.dotcmsUrl = getHostURL(config.dotcmsUrl)?.origin;
        if (!this.dotcmsUrl) {
            throw new Error("Invalid configuration - 'dotcmsUrl' must be a valid URL");
        }
        if (!config.authToken) {
            throw new Error("Invalid configuration - 'authToken' is required");
        }
        __classPrivateFieldSet(this, _DotCmsClient_config, {
            ...config,
            dotcmsUrl: this.dotcmsUrl
        }, "f");
        __classPrivateFieldSet(this, _DotCmsClient_requestOptions, {
            ...__classPrivateFieldGet(this, _DotCmsClient_config, "f").requestOptions,
            headers: {
                Authorization: `Bearer ${__classPrivateFieldGet(this, _DotCmsClient_config, "f").authToken}`,
                ...__classPrivateFieldGet(this, _DotCmsClient_config, "f").requestOptions?.headers
            }
        }, "f");
        this.content = new Content(__classPrivateFieldGet(this, _DotCmsClient_requestOptions, "f"), __classPrivateFieldGet(this, _DotCmsClient_config, "f").dotcmsUrl);
    }
    static init(config) {
        if (this.instance) {
            console.warn('DotCmsClient has already been initialized. Please use the instance to interact with the DotCMS API.');
        }
        return this.instance ?? (this.instance = new DotCmsClient(config));
    }
    static get dotcmsUrl() {
        return (this.instance && __classPrivateFieldGet(this.instance, _DotCmsClient_config, "f").dotcmsUrl) || '';
    }
    validatePageOptions(options) {
        if (!options.path) {
            throw new Error("The 'path' parameter is required for the Page API");
        }
    }
    validateNavOptions(options) {
        if (!options.path) {
            throw new Error("The 'path' parameter is required for the Nav API");
        }
    }
}
_DotCmsClient_config = new WeakMap(), _DotCmsClient_requestOptions = new WeakMap(), _DotCmsClient_listeners = new WeakMap();
